import { getDepositCashFlowNameFromParam } from 'features/moveMoney/utils/getDepositCashFlowNameFromParam';
import { createContext, type ReactNode, useContext, useState } from 'react';

export type DepositCashFlowName = 'move_money' | 'onboarding';

interface DepositCashFlowContextValue {
  flowName: DepositCashFlowName;
  setFlowName: (flowName?: DepositCashFlowName) => void;
}

interface DepositCashFlowInternalContextValue {
  initialFlowName: DepositCashFlowName;
  flowNameOverride?: DepositCashFlowName;
  setFlowNameOverride: (flowName?: DepositCashFlowName) => void;
}

const DepositCashFlowContext = createContext<
  DepositCashFlowInternalContextValue | undefined
>(undefined);

interface DepositCashFlowProviderProps {
  children: ReactNode;
  initialFlowName?: DepositCashFlowName;
}

export function DepositCashFlowProvider({
  children,
  initialFlowName = 'move_money',
}: DepositCashFlowProviderProps) {
  const [flowNameOverride, setFlowNameOverride] = useState<
    DepositCashFlowName | undefined
  >(undefined);

  return (
    <DepositCashFlowContext.Provider
      value={{ initialFlowName, flowNameOverride, setFlowNameOverride }}
    >
      {children}
    </DepositCashFlowContext.Provider>
  );
}

export function useDepositCashFlow(
  flowSource?: string | string[],
): DepositCashFlowContextValue {
  const context = useContext(DepositCashFlowContext);
  if (context === undefined) {
    return DefaultDepositCashFlowContext;
  }
  const derivedFlowNameFromRoute =
    flowSource === undefined
      ? context.initialFlowName
      : getDepositCashFlowNameFromParam(flowSource);
  const flowName = context.flowNameOverride ?? derivedFlowNameFromRoute;

  const setFlowName = (next?: DepositCashFlowName) => {
    context.setFlowNameOverride(next);
  };

  return { flowName, setFlowName };
}

const DefaultDepositCashFlowContext: DepositCashFlowContextValue = {
  flowName: 'move_money',
  setFlowName: () => {
    if (__DEV__) {
      console.warn('DepositCashFlowProvider missing; using default flow');
    }
  },
};
